/**
 * components/stat-plate — примитив «плашка-показатель», визуально = кнопка.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: owner-directive 2026-06-09 — в done-state доски дня
 * «над кнопками плашка, точно такая же как кнопка по размеру и шрифту, но в ней
 * "Ранг: N"». Это НОВЫЙ переиспользуемый атом → живёт примитивом в components/, а
 * не верстается в экране (code-structure §2 / §10.2; responsive §10.2). Любой
 * surface, которому нужна неинтерактивная плашка-показатель в ряду кнопок, монтирует
 * этот примитив.
 *
 * SCOPE: построение DOM плашки + a11y (`role="img"` + `aria-label`) + setValue.
 *   Размеры/шрифт — НЕ собственные: плашка несёт базовый класс `.btn` (тот же
 *   `--btn-h`/`--fs-*`-токен от `--cell-size` → масштабируется на 4К без хардкода)
 *   и, будучи внутри `.action-rail`-контейнера, попадает в ОБЩУЮ autofit-группу
 *   `.action-rail .btn` → её шрифт ИЗМЕРЯЕТСЯ единым с соседними кнопками (не
 *   угадан, responsive §8.10-A). Co-located CSS лишь снимает интерактивность.
 * NOT IN SCOPE: клики/навигация (плашка информационная), форматирование значения
 *   (caller форматирует и зовёт setValue).
 *
 * Cross-references:
 *   - .claude/skills/code-structure-conventions/SKILL.md §2 (примитивы), §10.2.
 *   - .claude/skills/responsive-ui-design/SKILL.md §10.2 / §8.10 (autofit-группа).
 *   - styles/global.css `.btn` (базовый размер/шрифт от токенов).
 *   - screens/daily-board-screen.ts (done-state: «Ранг: N»).
 */

import './stat-plate.css';

export interface StatPlateOptions {
  /**
   * Подпись показателя (например «Ранг»). Видимый текст = `${label}: ${value}`.
   * Опустить → плашка показывает только значение (например таймер «12:34:56»);
   * `ariaLabel` тогда стоит задать явно для доступности.
   */
  readonly label?: string;
  /** Явный aria-label (для плашки без подписи — иначе берётся из текста). */
  readonly ariaLabel?: string;
  /**
   * Начальное значение (по умолчанию «—», пока не пришли данные). Видимый текст =
   * `${label}: ${value}` либо `${value}` без подписи.
   */
  readonly initialValue?: string;
}

export interface StatPlateHandle {
  /** Корневой элемент плашки — caller кладёт его в контейнер кнопок. */
  readonly element: HTMLElement;
  /** Обновить значение (текст → `${label}: ${value}`, aria-label синхронно). */
  setValue(value: string): void;
}

/**
 * Создать плашку-показатель, визуально идентичную кнопке `.btn`. Неинтерактивна
 * (`role="img"`, без обработчиков). Текст — одной строкой `${label}: ${value}`;
 * размер шрифта даёт общая autofit-группа `.action-rail .btn` (caller монтирует
 * плашку в `.action-rail`-контейнер рядом с кнопками).
 */
export function createStatPlate(opts: StatPlateOptions): StatPlateHandle {
  const label = opts.label;
  let value = opts.initialValue ?? '—';

  // База `.btn` → идентичные кнопке размер/шрифт/радиус из токенов (без хардкода).
  // `.stat-plate` снимает hover/active/cursor (co-located CSS). role=img + aria-label
  // сообщают, что это статус, а не кнопка (a11y зашита в примитив, §2).
  const element = document.createElement('div');
  element.className = 'btn stat-plate';
  element.setAttribute('role', 'img');

  const render = (): void => {
    const text = label ? `${label}: ${value}` : value;
    element.textContent = text;
    element.setAttribute('aria-label', opts.ariaLabel ?? text);
  };
  render();

  return {
    element,
    setValue(next: string): void {
      value = next;
      render();
    },
  };
}
